import React, { ReactNode } from "react";
import { ControlGroup } from "./layout/ControlGroup";
import { PointDrawButton } from "./element/drawing/PointDrawButton";
import { PolylineDrawButton } from "./element/drawing/PolylineDrawButton";
import { RectangleDrawButton } from "./element/drawing/RectangleDrawButton";
import { PolygonDrawButton } from "./element/drawing/PolygonDrawButton";
import { TextDrawButton } from "./element/drawing/TextDrawButton";
import { ModifyAnnotation } from "./element/function/ModifyAnnotation";
import { MoveAnnotation } from "./element/function/MoveAnnotation";
import { DeleteAnnotation } from "./element/function/DeleteAnnotation";
import { MultiPointDrawButton } from "./element/drawing/MultiPointDrawButton";
import { MapToolGuideChip } from "./MapToolGuideChip";
import { Geometry } from "ol/geom";
import Feature from "ol/Feature";
import { TranslateEvent } from "ol/interaction/Translate";
import { ModifyEvent } from "ol/interaction/Modify";
import { ANNOTATION_COLOR } from "../constants";
import { FeatureFromGeojson } from "../FeaturesStore";
import { useTranslation } from "react-i18next";
import { MAP_TOOL_BUTTON } from "src/domains/shared/constants/mapToolButton";

/** 개별 도구의 활성 상태 — `true` 활성, `false` 숨김, `"disabled"` 비활성. */
type FeatureMode = boolean | "disabled";

/**
 * 어노테이션 드로잉/편집 툴바 props — `<FeaturesStore>` + `<SelectedFeatureStore>` 내부에서 사용.
 *
 * 각 도구(marker/polyline/rectangle/polygon/text/multiMarker)는 활성/비활성/숨김 3 상태를 지원.
 * 그린 어노테이션의 색상은 `color` (`ANNOTATION_COLOR` 키)로 통일된다.
 */
export interface DrawingToolsProps {
  multiMarker?: FeatureMode;
  marker?: FeatureMode;
  polyline?: FeatureMode;
  rectangle?: FeatureMode;
  polygon?: FeatureMode;
  text?: FeatureMode;
  target?: Feature<Geometry> | null;
  edit?: FeatureMode;
  movement?: FeatureMode;
  color?: keyof typeof ANNOTATION_COLOR;
  remove?: FeatureMode;
  onDelete?: (event: FeatureFromGeojson | undefined) => void;
  onMove?: (event: TranslateEvent) => void;
  onModify?: (event: ModifyEvent) => void;
  onDrawEnd?: (event: FeatureFromGeojson) => void;
  onCanvas?: boolean;
  children?: ReactNode;
  arrange?: "vertical" | "horizontal";
}

export function DrawingTools({
  color = "BLUE",
  target = null,
  multiMarker = true,
  marker = true,
  polyline = true,
  rectangle = true,
  polygon = true,
  text = true,
  edit = true,
  movement = true,
  remove = true,
  onCanvas = false,
  onDrawEnd,
  onDelete,
  onMove,
  onModify,
  children,
  arrange = "vertical",
}: DrawingToolsProps) {
    const { t } = useTranslation("timeSeries");

  //     setIsSelected(key);
  //   }
  // };

  const endDrawing = (event: FeatureFromGeojson) => {
    if (onDrawEnd) {
      onDrawEnd(event);
    }
    // setIsSelected(null);
  };


  return (
    <div
      style={
        arrange === "horizontal"
          ? { display: "flex", gap: `${MAP_TOOL_BUTTON.GROUP_GAP_PX}px` }
          : {}
      }
    >
      <MapToolGuideChip />
      <ControlGroup title={t("drawingTools.draw", { ns: "timeSeries" })}>
        {multiMarker === true && (
          <MultiPointDrawButton
            onEnd={endDrawing}
            onCanvas={onCanvas}
          />
        )}
        {multiMarker === "disabled" && (
          <MultiPointDrawButton
            disabled
            onEnd={endDrawing}
            onCanvas={onCanvas}
          />
        )}
        {marker === true && (
          <PointDrawButton
            onEnd={endDrawing}
            onCanvas={onCanvas}
          />
        )}
        {marker === "disabled" && (
          <PointDrawButton
            disabled
            onEnd={endDrawing}
            onCanvas={onCanvas}
          />
        )}
        {polyline === true && (
          <PolylineDrawButton
            color={color}
            onEnd={endDrawing}
            onCanvas={onCanvas}
          />
        )}
        {polyline === "disabled" && (
          <PolylineDrawButton
            disabled
            color={color}
            onEnd={endDrawing}
            onCanvas={onCanvas}
          />
        )}
        {rectangle === true && (
          <RectangleDrawButton
            color={color}
            onEnd={endDrawing}
            onCanvas={onCanvas}
          />
        )}
        {rectangle === "disabled" && (
          <RectangleDrawButton
            disabled
            color={color}
            onEnd={endDrawing}
            onCanvas={onCanvas}
          />
        )}
        {polygon === true && (
          <PolygonDrawButton
            color={color}
            onEnd={endDrawing}
            onCanvas={onCanvas}
          />
        )}
        {polygon === "disabled" && (
          <PolygonDrawButton
            disabled
            color={color}
            onEnd={endDrawing}
            onCanvas={onCanvas}
          />
        )}
        {text === true && (
          <TextDrawButton
            onEnd={endDrawing}
            onCanvas={onCanvas}
          />
        )}
        {text === "disabled" && (
          <TextDrawButton
            disabled
            onEnd={endDrawing}
            onCanvas={onCanvas}
          />
        )}
      </ControlGroup>

      <div style={sideColumnStyle}>
        <ControlGroup title={t("drawingTools.edit", { ns: "timeSeries" })}>
          {edit === true && (
            <ModifyAnnotation
              target={target}
              onModifyChange={onModify}
            />
          )}
          {edit === "disabled" && (
            <ModifyAnnotation
              disabled
              target={target}
              onModifyChange={onModify}
            />
          )}
          {movement === true && (
            <MoveAnnotation
              target={target}
              onMoveChange={onMove}
            />
          )}

          {remove === true && (
            <DeleteAnnotation
              onDeleteChange={onDelete}
            />
          )}
          {remove === "disabled" && (
            <DeleteAnnotation
              disabled
              onDeleteChange={onDelete}
            />
          )}
        </ControlGroup>
        {children}
      </div>
    </div>
  );
}

/** 편집 그룹과 그 아래 기타 그룹은 생성·편집 열 간격과 같은 값으로 띄운다 */
const sideColumnStyle = {
  display: "flex",
  flexDirection: "column",
  gap: `${MAP_TOOL_BUTTON.GROUP_GAP_PX}px`,
} as const;
